import { useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles,
  UploadCloud,
  Link2,
  FileUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { addEvaluationRecord, CREATE_DIMENSION_OPTIONS, type EvaluationRecord, type KindLabel } from "../mocks/evaluation-records";
import type { ProgressNode } from "../components/ProgressBoard";
import { ProgressBoard } from "../components/ProgressBoard";

type Step = 1 | 2 | 3 | 4;

type EvaluationInput =
  | { kind: "file"; fileName: string }
  | { kind: "text"; text: string }
  | { kind: "link"; url: string };

export default function InnovationCreateWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<Step>(1);
  const [kinds, setKinds] = useState<KindLabel[]>([]);
  const [input, setInput] = useState<EvaluationInput | null>(null);
  const [textDraft, setTextDraft] = useState("");
  const [linkDraft, setLinkDraft] = useState("");
  const [running, setRunning] = useState(false);
  const [nodes, setNodes] = useState<ProgressNode[]>([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const isDone = nodes.some((n) => n.id === "done" && n.status === "done");

  const canGoDimension = Boolean(input);
  const canRun = Boolean(input && kinds.length > 0);

  // 支持从概览页「可用评价维度」带参直达：/innovation/create?dimension=xxx
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const raw = qs.get("dimension");
    if (!raw) return;
    const dim = raw.trim();
    if (!dim) return;
    const opt = CREATE_DIMENSION_OPTIONS.find((d) => d.title === dim || d.kind === dim);
    if (opt) setKinds((prev) => (prev.includes(opt.kind) ? prev : [opt.kind, ...prev]));
  }, [location.search]);

  const initNodes = (): ProgressNode[] => [
    { id: "parse", title: "输入解析", desc: "提取摘要 / 元信息 / 结构化字段", status: "queued", logs: [] },
    { id: "retrieve", title: "文献与对标检索", desc: "检索同类项目 / 论文 / 专利", status: "queued", logs: [] },
    { id: "score", title: "维度打分", desc: "按所选维度生成分项评分", status: "queued", logs: [] },
    { id: "findings", title: "关键发现生成", desc: "优势 / 风险 / 关键问题", status: "queued", logs: [] },
    { id: "compose", title: "报告排版", desc: "输出报告结构与行动路径", status: "queued", logs: [] },
    { id: "done", title: "完成", desc: "生成并可保存为记录", status: "queued", logs: [] },
  ];

  const startRun = () => {
    if (running) return;
    if (!canRun) return;
    setStep(3);
    window.setTimeout(() => run(), 0);
  };

  const run = () => {
    if (running) return;
    const createdAt = Date.now();
    setRunning(true);
    setNodes(initNodes());

    const advance = (id: string, nextStatus: ProgressNode["status"], extraLogs?: string[]) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== id) return n;
          const logs = [...(n.logs || [])];
          if (extraLogs?.length) logs.push(...extraLogs);
          const costMs = nextStatus === "done" ? Date.now() - createdAt : n.costMs;
          return { ...n, status: nextStatus, logs, costMs: nextStatus === "done" ? costMs : n.costMs };
        }),
      );
    };

    const setRunningNode = (id: string, extraLogs?: string[]) => advance(id, "running", extraLogs);
    const setDoneNode = (id: string, extraLogs?: string[]) => advance(id, "done", extraLogs);

    window.setTimeout(() => setRunningNode("parse", ["开始解析输入材料…"]), 120);
    window.setTimeout(() => setDoneNode("parse", ["解析完成：提取摘要/关键词/章节结构（演示）"]), 900);

    window.setTimeout(() => setRunningNode("retrieve", ["检索对标样本：同类项目/论文/专利（演示）"]), 980);
    window.setTimeout(() => setDoneNode("retrieve", ["命中样本 12 条，生成差异化证据链（演示）"]), 1800);

    window.setTimeout(() => setRunningNode("score", [`按维度「${kinds.join("、")}」开始打分…`]), 1860);
    window.setTimeout(() => setDoneNode("score", ["分项评分已生成：创新性/可行性/对标（演示）"]), 2600);

    window.setTimeout(() => setRunningNode("findings", ["生成关键发现：优势/风险/关键问题…"]), 2660);
    window.setTimeout(() => setDoneNode("findings", ["关键发现已生成，形成可执行修改清单（演示）"]), 3400);

    window.setTimeout(() => setRunningNode("compose", ["编排报告结构与行动路径…"]), 3460);
    window.setTimeout(() => setDoneNode("compose", ["报告排版完成：摘要/评分/发现/建议（演示）"]), 4100);

    window.setTimeout(() => setDoneNode("done", ["运行完成，可查看报告并保存为评价记录。"]), 4300);
    window.setTimeout(() => {
      setRunning(false);
    }, 4400);
  };

  const makeRecord = (): EvaluationRecord => {
    const id = `eval-${Date.now()}`;
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const title =
      input?.kind === "file"
        ? input.fileName.replace(/\.[^/.]+$/, "")
        : input?.kind === "link"
          ? "链接输入内容"
          : input?.kind === "text"
            ? "文本输入内容"
            : "未命名研究内容";

    const summary = "综合评价结果已生成（演示）。建议优先按行动路径执行：补齐对标样本、量化指标口径，并完善风险分级说明。";

    const inputContent =
      input?.kind === "file"
        ? `（演示）已上传文件：${input.fileName}`
        : input?.kind === "text"
          ? input.text
          : input?.kind === "link"
            ? `（演示）已输入链接：${input.url}`
            : "（演示）未输入材料。";

    const dimLine = kinds.length ? `\n（演示）已选维度：${kinds.join("、")}` : "";
    const primaryKind = (kinds[0] ?? "项目综合评价") as KindLabel;

    return {
      id,
      title,
      kind: primaryKind,
      date: `${yyyy}-${mm}-${dd}`,
      score: 85,
      status: "已完成",
      summary,
      dimensions: [
        { label: "创新性", score: 89, note: "方向前沿、首创性较强" },
        { label: "可行性", score: 83, note: "路径清晰，仍需补齐验证口径" },
        { label: "对标", score: 92, note: "建议补充样本列表与检索范围" },
      ],
      highlights: ["前沿问题拆解清晰，论证链条完整", "与国家专项契合度高，窗口期论证较充分"],
      improvements: ["将创新点表述收敛为 3 条主线", "补齐对标样本与检索口径披露"],
      report: {
        inputTitle: "输入内容",
        inputContent: `${inputContent}${dimLine}`,
        scoreBars: [
          { label: "创新性", score: 89, tone: "primary" },
          { label: "可行性", score: 83, tone: "primary" },
          { label: "对标", score: 92, tone: "primary" },
        ],
        conclusion: summary,
        keyIssues: [
          "对标样本范围与口径需统一披露（同类项目 / 论文 / 专利）",
          "关键指标需量化并绑定里程碑验收口径",
          "风险分级与应对策略需更可操作",
        ],
        advantages: ["论证链条清晰，证据引用可追溯", "技术路线分阶段可执行", "跨学科结合带来差异化"],
        risks: ["对标披露不足导致质疑", "指标口径不清导致验收困难", "合规条款需提前审查"],
      },
    };
  };

  const saveAndOpenRecord = () => {
    const rec = makeRecord();
    addEvaluationRecord(rec);
    setCreatedId(rec.id);
    setReportOpen(false);
    navigate(`/innovation/records/${rec.id}`);
  };

  return (
    <div className="pb-10">
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">输入材料</h1>
            <p className="mt-2 text-sm text-muted-foreground">上传文件、粘贴文本或输入链接，作为创新评价的输入材料。</p>
          </div>

          <div className="rounded-2xl border border-dashed border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                  <UploadCloud className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">上传文件</div>
                  <div className="mt-1 text-xs text-muted-foreground">支持 PDF、Word、TXT（演示：不读取真实文件内容）</div>
                </div>
              </div>
              <Button variant="outline" onClick={() => setInput({ kind: "file", fileName: "research-content.pdf" })}>
                选择文件
              </Button>
            </div>
            {input?.kind === "file" ? <div className="mt-3 text-xs text-muted-foreground">已选择：{input.fileName}</div> : null}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                粘贴文本
              </div>
              <textarea
                value={textDraft}
                onChange={(e) => {
                  setTextDraft(e.target.value);
                  if (e.target.value.trim()) setInput({ kind: "text", text: e.target.value.trim() });
                }}
                rows={6}
                placeholder="粘贴研究内容/摘要/申报书片段…"
                className="mt-3 w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-7 text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
              />
              <div className="mt-2 text-xs text-muted-foreground">建议：用空行分段，越接近原文越好。</div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Link2 className="h-4 w-4 text-primary" />
                输入链接
              </div>
              <input
                value={linkDraft}
                onChange={(e) => {
                  setLinkDraft(e.target.value);
                  const v = e.target.value.trim();
                  if (v) setInput({ kind: "link", url: v });
                }}
                placeholder="例如：https://..."
                className="mt-3 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
              />
              <div className="mt-2 text-xs text-muted-foreground">演示：仅保存链接，不抓取网页内容。</div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">已输入 {input ? "1" : "0"} 份材料</span>
            <Button disabled={!canGoDimension} onClick={() => setStep(2)}>
              下一步：选择维度
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">选择评价维度</h1>
            <p className="mt-2 text-sm text-muted-foreground">选择需要评估的维度，系统将按该维度生成进度看板并输出评价报告。</p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {CREATE_DIMENSION_OPTIONS.map((d) => {
              const Icon = d.Icon;
              const active = kinds.includes(d.kind);
              return (
                <button
                  key={d.kind}
                  type="button"
                  onClick={() =>
                    setKinds((prev) => (prev.includes(d.kind) ? prev.filter((x) => x !== d.kind) : [...prev, d.kind]))
                  }
                  className={cn(
                    "flex items-start justify-between gap-4 rounded-2xl border bg-card p-5 text-left shadow-sm transition-all hover:border-primary/30",
                    active ? "border-primary/40 ring-2 ring-primary/15" : "border-border",
                  )}
                >
                  <div className="flex min-w-0 gap-4">
                    <div className={cn("mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", d.wrap)}>
                      <Icon className={cn("h-5 w-5", d.icolor)} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground">{d.title}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {d.tags.map((t) => (
                          <span key={t} className={cn("rounded-full px-2.5 py-1 text-xs font-medium", d.wrap, d.tcolor)}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                      active ? "border-primary bg-primary/10" : "border-border bg-background",
                    )}
                  >
                    {active && <span className="h-2.5 w-2.5 rounded-sm bg-primary" />}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-4">
            <Button variant="outline" onClick={() => setStep(1)}>
              上一步：输入材料
            </Button>
            <Button disabled={!canRun} onClick={startRun}>
              开始运行评价
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">新建创新评价</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span>输入材料</span>
                <span className="text-muted-foreground/40">·</span>
                <span>维度：{kinds.length ? kinds.join("、") : "—"}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setStep(2)} disabled={running}>
                返回维度选择
              </Button>
            </div>
          </div>

          <div className="p-5">
            <ProgressBoard nodes={nodes} />

            {isDone ? (
              <div className="mt-6 rounded-2xl border border-border bg-background p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold text-foreground">评价报告已生成</div>
                    <div className="mt-1 text-sm text-muted-foreground">结果已在下方展示，你可以直接保存为记录并继续迭代。</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={run} disabled={running}>
                      重新运行
                    </Button>
                    <Button onClick={saveAndOpenRecord} className="gap-2">
                      保存为评价记录
                    </Button>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <MetricCard tone="blue" big="85%" label="综合评价结果" />
                  <MetricCard tone="green" big="中低" label="风险等级" />
                  <MetricCard tone="amber" big="23" label="关键信息提取" suffix="条" />
                  <MetricCard tone="rose" big="1" label="风险预警" suffix="项" />
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="text-sm font-semibold text-foreground">输入材料</div>
                    <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                      {input?.kind === "file"
                        ? input.fileName
                        : input?.kind === "link"
                          ? input.url
                          : input?.kind === "text"
                            ? `${input.text.slice(0, 240)}${input.text.length > 240 ? "…" : ""}`
                            : "—"}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="text-sm font-semibold text-foreground">已选维度</div>
                    <div className="mt-2 text-sm text-muted-foreground">{kinds.length ? kinds.join("、") : "—"}</div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-sm font-semibold text-foreground">已生成评价结果</div>
                  <div className="mt-3 rounded-xl bg-muted/30 p-4 text-sm text-muted-foreground">
                    <div className="font-medium text-foreground">创新决策报告</div>
                    <div className="mt-1 text-xs">综合得分 85 · 关键参考依据 23 条 · 风险预警 1 项</div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-sm font-semibold text-foreground">待执行行动路径</div>
                  <div className="mt-3 space-y-2">
                    <PathRow tone="rose" title="立即" desc="补齐对标样本范围与检索口径披露，强化差异化证据链" />
                    <PathRow tone="amber" title="近期" desc="将关键指标量化并绑定里程碑验收口径，补充对照实验设计" />
                    <PathRow tone="blue" title="中期" desc="优化创新点表述结构，收敛为 3 条主线并给出可复核证据" />
                    <PathRow tone="green" title="长期" desc="建立持续评价机制，结合项目节点自动提醒并迭代报告版本" />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-[720px] p-0">
          <div className="p-6">
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="text-base">创新评价综合报告</DialogTitle>
              <div className="text-xs text-muted-foreground">2026-04-11 · 研究风险等级：中低</div>
            </DialogHeader>

            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              <MetricCard tone="blue" big="85%" label="综合评价结果" />
              <MetricCard tone="green" big="中低" label="风险等级" />
              <MetricCard tone="amber" big="23" label="关键信息提取" suffix="条" />
              <MetricCard tone="rose" big="1" label="风险预警" suffix="项" />
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-foreground">已生成评价结果</div>
              <div className="mt-3 rounded-xl bg-muted/30 p-4 text-sm text-muted-foreground">
                <div className="font-medium text-foreground">创新决策报告</div>
                <div className="mt-1 text-xs">综合得分 85 · 关键参考依据 23 条 · 风险预警 1 项</div>
              </div>
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-foreground">待执行行动路径</div>
              <div className="mt-3 space-y-2">
                <PathRow tone="rose" title="立即" desc="补齐对标样本范围与检索口径披露，强化差异化证据链" />
                <PathRow tone="amber" title="近期" desc="将关键指标量化并绑定里程碑验收口径，补充对照实验设计" />
                <PathRow tone="blue" title="中期" desc="优化创新点表述结构，收敛为 3 条主线并给出可复核证据" />
                <PathRow tone="green" title="长期" desc="建立持续评价机制，结合项目节点自动提醒并迭代报告版本" />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => setReportOpen(false)}>
              关闭
            </Button>
            <Button onClick={saveAndOpenRecord}>保存为评价记录</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({
  tone,
  big,
  label,
  suffix,
}: {
  tone: "blue" | "green" | "amber" | "rose";
  big: string;
  label: string;
  suffix?: string;
}) {
  const cls = tone === "amber" || tone === "rose" ? "text-muted-foreground" : "text-foreground";
  return (
    <div className={cn("rounded-xl border border-border bg-muted/20 p-3", cls)}>
      <div className="text-2xl font-bold">
        {big}
        {suffix ? <span className="ml-1 text-xs font-semibold">{suffix}</span> : null}
      </div>
      <div className="mt-1 text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

function PathRow({
  tone,
  title,
  desc,
}: {
  tone: "rose" | "amber" | "blue" | "green";
  title: string;
  desc: string;
}) {
  const cls = "border border-border bg-muted/20";
  const dot = tone === "rose" ? "bg-destructive" : "bg-primary";
  return (
    <div className={cn("flex items-start gap-3 rounded-xl p-3 text-sm text-muted-foreground", cls)}>
      <span className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-full", dot)} />
      <div className="min-w-0">
        <div className="font-medium text-foreground">{title}</div>
        <div className="mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
